import { HomeIcon } from '@heroicons/react/20/solid';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { BLUR_FADE_DELAY } from '@/components/magicui/blur-fade-text';
import BlurFade from '@/components/magicui/blur-fade';
import { MOBILE_SCREEN_SIZE } from '@/utils/constants';
import { useMediaQuery } from 'usehooks-ts';
import { MoveRight } from 'lucide-react';

const HOME_BREADCRUMB = { url: '/', text: 'Simplicity® Business Software' };
const COMMON_UTILITY_BREADCRUMB = { url: '#', text: 'common utilities' };
const COMMON_UTILITY_PAGES = [
  'decision support modules',
  'application configuration tools',
];

const getPathSegments = (pathname: string) =>
  pathname.split('/').filter(Boolean);

const getBreadcrumbs = (pathSegments: string[]) => {
  return pathSegments.map((segment, index) => ({
    url: `/${pathSegments.slice(0, index + 1).join('/')}/`,
    text: segment.replace(/-/g, ' '),
  }));
};

const generateBreadcrumbs = (pathname: string) => {
  const pathSegments = getPathSegments(pathname);
  const breadcrumbs = getBreadcrumbs(pathSegments);

  if (!breadcrumbs.length) {
    return [HOME_BREADCRUMB];
  }
  if (COMMON_UTILITY_PAGES.includes(breadcrumbs[1]?.text)) {
    breadcrumbs.splice(1, 0, COMMON_UTILITY_BREADCRUMB);
  }
  return breadcrumbs;
};

const Breadcrumb = () => {
  const pathname = usePathname();
  const breadcrumbs = generateBreadcrumbs(pathname);
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_SCREEN_SIZE}px)`);

  return (
    <BlurFade duration={BLUR_FADE_DELAY}>
      <nav aria-label="Breadcrumb" className="flex">
        <ol
          role="list"
          className={`flex ${isMobile ? 'space-x-1' : 'space-x-2'} items-end`}
        >
          <li className="align-bottom flex">
            <a
              href="#"
              className={`${isMobile ? '' : 'text-gray-500'} hover:text-gray-700`}
            >
              {isMobile ? (
                <span className="text-[9px] lg:text-xs xl:text-sm hover:underline capitalize">
                  Home
                </span>
              ) : (
                <HomeIcon aria-hidden="true" className="h-5 w-5" />
              )}

              <span className="sr-only">Home</span>
            </a>
          </li>
          {breadcrumbs.map((page) => (
            <li key={page.url}>
              <div
                className={`flex  ${isMobile ? 'space-x-1 items-baseline' : 'space-x-2 items-end'}`}
              >
                {isMobile ? (
                  <span className="text-[9px]">/</span>
                ) : (
                  <svg
                    fill="currentColor"
                    viewBox="0 0 22 22"
                    aria-hidden="true"
                    className="h-5 w-5 text-gray-300"
                  >
                    <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                  </svg>
                )}
                <Link
                  href={page.url}
                  className={`${isMobile ? 'ml-1 text-[9px]' : 'ml-2 text-xs'} leading-[1.5] lg:text-xs xl:text-sm hover:underline capitalize`}
                >
                  {page.text}
                </Link>
              </div>
            </li>
          ))}
        </ol>
      </nav>
    </BlurFade>
  );
};

export default Breadcrumb;
